import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Page, Spinner, TextButton, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { helpArticles } from '~/utils/helpArticles';
import { loadZapierJsAndCss } from '~/utils/zapier-elements';
import styles from './SlackViaZapierPage.module.scss';
var LoadZapierStatus = /*#__PURE__*/ function(LoadZapierStatus) {
    LoadZapierStatus["Loading"] = "loading";
    LoadZapierStatus["Success"] = "success";
    LoadZapierStatus["Error"] = "error";
    return LoadZapierStatus;
}(LoadZapierStatus || {});
export const GetSlackViaZapierPageTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            SlackViaZapierPageTID: {
                Root: 'slack-via-zapier',
                ZapTemplates: 'zapier-zap-templates',
                Loading: 'zapier-loading',
                Error: 'zapier-error'
            }
        }
    });
const uiMap = {
    ["success"]: function SuccessState() {
        const userEmail = useStore(selectors.user.email);
        const userFirstName = useStore(selectors.user.firstName);
        const userLastName = useStore(selectors.user.lastName);
        const { SlackViaZapierPageTID } = useMemo(()=>GetSlackViaZapierPageTID(), []);
        return /*#__PURE__*/ _jsx(Card, {
            bi: {
                name: 'slack_via_zapier_card'
            },
            autoHeight: true,
            padding: "m",
            children: /*#__PURE__*/ _jsx("zapier-zap-templates", {
                "data-testid": SlackViaZapierPageTID.ZapTemplates(),
                "sign-up-email": userEmail,
                "sign-up-first-name": userFirstName,
                "sign-up-last-name": userLastName,
                "client-id": "ieG0DJXKRhT4gI3t1DfefyH3YmooofjSMrEecI1t",
                theme: "light",
                ids: "1308170,1230341,1223023,1307798,1306468,1388914,1233302,1616853",
                limit: 5,
                "use-this-zap": "show"
            })
        });
    },
    ["loading"]: function LoadingState() {
        const { SlackViaZapierPageTID } = useMemo(()=>GetSlackViaZapierPageTID(), []);
        return /*#__PURE__*/ _jsx("div", {
            className: styles.centeredBox,
            "data-testid": SlackViaZapierPageTID.Loading(),
            children: /*#__PURE__*/ _jsx(Spinner, {})
        });
    },
    ["error"]: function ErrorState() {
        const { t: slackTexts } = useLocaleKeys('integrations.setup.slack');
        const { browser: { locationReload } } = useIoContext();
        const { SlackViaZapierPageTID } = useMemo(()=>GetSlackViaZapierPageTID(), []);
        return /*#__PURE__*/ _jsx("div", {
            className: styles.centeredBox,
            "data-testid": SlackViaZapierPageTID.Error(),
            children: /*#__PURE__*/ _jsxs(Typography, {
                children: [
                    slackTexts.errorOccurred(),
                    ' ',
                    /*#__PURE__*/ _jsx(TextButton, {
                        bi: {
                            name: 'reload_slack_integration_status'
                        },
                        onClick: ()=>locationReload(),
                        children: slackTexts.reload()
                    }),
                    ' ',
                    slackTexts.toTryAgain()
                ]
            })
        });
    }
};
export default function SlackViaZapierPage() {
    const { t: availableAppsSlackTexts } = useLocaleKeys('integrations.apps.slack');
    const { t: slackTexts } = useLocaleKeys('integrations.setup.slack');
    const { browser: { injectScriptOrLink } } = useIoContext();
    const navigate = useNavigate();
    const [loadZapierStatus, setLoadZapierStatus] = useState(null);
    const { SlackViaZapierPageTID } = useMemo(()=>GetSlackViaZapierPageTID(), []);
    useOnMount(()=>{
        setLoadZapierStatus("loading");
        loadZapierJsAndCss({
            injectScriptOrLink
        }).then(()=>setLoadZapierStatus("success")).catch(()=>setLoadZapierStatus("error"));
    });
    return /*#__PURE__*/ _jsx(Page, {
        testId: SlackViaZapierPageTID.Root(),
        header: availableAppsSlackTexts.recommendedZaps(),
        subheader: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                "".concat(slackTexts.automateWorkflows(), " "),
                /*#__PURE__*/ _jsx(ExternalLinkButton, {
                    openInNewTab: true,
                    href: helpArticles.anchorZaps(),
                    bi: {
                        name: 'slack_anchor_zaps_read_more'
                    },
                    children: slackTexts.readMore()
                })
            ]
        }),
        onClickBack: ()=>navigate(routes.availableApps),
        children: loadZapierStatus && /*#__PURE__*/ createElement(uiMap[loadZapierStatus])
    });
}
